import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useTranslation } from 'react-i18next';

// Utility to translate backend breakdown keys into human-readable labels
const translateLabel = (key) => {
  const map = {
    birth_number: 'Rodné číslo',
    birth_numbers: 'Rodné čísla',
    name: 'Meno / Priezvisko',
    names: 'Mená / Priezviská',
    birth_date: 'Dátum narodenia',
    birth_dates: 'Dátumy narodenia',
    account_number: 'Číslo účtu / IBAN',
    account_numbers: 'Čísla účtov / IBAN',
    address: 'Adresa',
    addresses: 'Adresy',
    phone: 'Telefón',
    phones: 'Telefónne čísla',
    email: 'E-mail',
    emails: 'E-maily',
    id_number: 'Číslo OP',
    id_numbers: 'Čísla OP',
    passport_number: 'Číslo pasu',
    passport_numbers: 'Čísla pasov',
    vehicle_ids: 'Údaje o vozidle (VIN, EČV)',
    vin: 'VIN vozidla',
    license_plate: 'EČV',
    custom: 'Vlastné slová/frázy',
    cenzor_detected_entities: 'Detegované entity (Cenzor)',
    signatures: 'Podpisy',
    stamps: 'Pečiatky',
  };
  return map[key] || key;
};

// Tri hlavné skupiny ukazovateľov, ktoré dokument musí "prejsť"
const GROUP_DEFS = [
  { key: 'gdpr', label: 'GDPR', sub: 'Osobné údaje', Icon: BadgeOutlinedIcon },
  { key: 'nis2', label: 'NIS2', sub: 'Kyberbezpečnosť', Icon: GppMaybeOutlinedIcon },
  { key: 'vyhradene', label: 'Vyhradené', sub: 'Utajované skutočnosti', Icon: LockOutlinedIcon },
];

// Jedna karta súhrnu skupiny – koľko ukazovateľov v nej NEPREŠLO kontrolou
const GroupStatCard = ({ def, count }) => {
  const failed = Number(count) > 0;
  const palette = failed ? 'error' : 'success';
  const { Icon } = def;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: (th) => alpha(th.palette[palette].main, 0.4),
        bgcolor: (th) => alpha(th.palette[palette].main, 0.08),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 0.25,
      }}
    >
      <Icon sx={{ color: `${palette}.main` }} fontSize="small" />
      <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
        {def.label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: `${palette}.main`, lineHeight: 1.1 }}>
        {Number(count) || 0}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
        {failed ? 'neprešlo' : 'v poriadku'}
      </Typography>
    </Box>
  );
};

// Rozpis podskupín (NIS2 / Vyhradené) – {total, groups:{<label>:{total, detectors}}}
const GroupSummaryDetail = ({ title, summary }) => {
  const groups = Object.entries(summary?.groups || {}).filter(([, g]) => Number(g?.total) > 0);
  if (!groups.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 2 }}>
        <Table size="small">
          <TableBody>
            {groups.map(([name, g]) => (
              <TableRow key={name} hover>
                <TableCell>{name}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {g.total}×
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ValidationReportModal = ({ open, report, fileObj, onClose, onAnonymize, onPseudonymize }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null); // 'anon' | 'pseudo' | null
  const [actionError, setActionError] = useState(null);

  if (!report) return null;

  const { filename, breakdown = {}, pages } = report;
  const fname = filename || fileObj?.name || 'Neznámy súbor';
  const isDocx = fname.toLowerCase().endsWith('.docx');

  const counts = {
    gdpr: Number(report.gdpr_values_count || 0),
    nis2: Number(report.nis2_values_count ?? report.nis2?.total ?? 0),
    vyhradene: Number(report.vyhradene_values_count ?? report.vyhradene?.total ?? 0),
  };
  const totalFailed = counts.gdpr + counts.nis2 + counts.vyhradene;
  const failedGroups = GROUP_DEFS.filter((d) => counts[d.key] > 0).length;
  const shouldNotShare = report.should_not_share ?? totalFailed > 0;

  const breakdownEntries = Object.entries(breakdown).filter(([, count]) => Number(count) > 0);

  const runAction = async (type, fn) => {
    if (!fn || !fileObj) return;
    setBusy(type);
    setActionError(null);
    try {
      const res = await fn(fileObj);
      if (res && res.success === false) {
        setActionError(typeof res.error === 'string' ? res.error : 'Operácia zlyhala.');
      }
    } catch (e) {
      setActionError(e?.message || 'Operácia zlyhala.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
        {t('ValidationReportModal.title', 'Výsledok kontroly')}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        {/* File Info Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Súbor:
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-word', fontWeight: 600 }}>
            {fname}
          </Typography>
          {pages > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Počet strán: {pages}
            </Typography>
          )}
        </Box>

        {/* Status Alert */}
        {shouldNotShare ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>Dokument nie je vhodné zdieľať</AlertTitle>
            Kontrolou neprešlo spolu <b>{totalFailed}</b>{' '}
            {totalFailed === 1 ? 'ukazovateľ' : totalFailed >= 2 && totalFailed <= 4 ? 'ukazovatele' : 'ukazovateľov'} v{' '}
            <b>{failedGroups}</b> {failedGroups === 1 ? 'skupine' : 'skupinách'}. Pred zdieľaním odporúčame údaje
            anonymizovať alebo pseudonymizovať.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 600 }}>Vyzerá to v poriadku</AlertTitle>
            Vo všetkých troch skupinách (GDPR, NIS2, Vyhradené) neprešli žiadne ukazovatele.
          </Alert>
        )}

        {/* 3-group summary */}
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          Ukazovatele, ktoré neprešli
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
          {GROUP_DEFS.map((def) => (
            <GroupStatCard key={def.key} def={def} count={counts[def.key]} />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          GDPR = osobné údaje · NIS2 = kyberbezpečnosť / technické údaje · Vyhradené = utajované skutočnosti
        </Typography>

        {/* GDPR breakdown */}
        {breakdownEntries.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Rozpis GDPR nálezov
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Typ údaju</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Počet
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdownEntries.map(([key, count]) => (
                    <TableRow key={key} hover>
                      <TableCell>{translateLabel(key)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {count}×
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* NIS2 + Vyhradené sub-breakdown */}
        <GroupSummaryDetail title="Rozpis NIS2 (kyberbezpečnosť)" summary={report.nis2} />
        <GroupSummaryDetail title="Rozpis Vyhradené (utajované skutočnosti)" summary={report.vyhradene} />

        {actionError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {actionError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={!!busy}
          color="inherit"
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {t('common.close', 'Zavrieť')}
        </Button>
        <Box sx={{ flex: 1 }} />
        {isDocx && onPseudonymize && (
          <Button
            onClick={() => runAction('pseudo', onPseudonymize)}
            disabled={!!busy}
            variant="outlined"
            color="primary"
            disableElevation
            startIcon={busy === 'pseudo' ? <CircularProgress size={16} /> : null}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {busy === 'pseudo' ? 'Pseudonymizujem…' : 'Pseudonymizovať'}
          </Button>
        )}
        {onAnonymize && (
          <Button
            onClick={() => runAction('anon', onAnonymize)}
            disabled={!!busy}
            variant="contained"
            color="primary"
            disableElevation
            startIcon={busy === 'anon' ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {busy === 'anon' ? 'Anonymizujem…' : 'Anonymizovať'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ValidationReportModal;
